import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { Pillar, SteeringReportInput } from 'chery-contracts';
import { currentQuarterWeekIds, formatValue, isNumericUnit, weeksBetween } from 'chery-kpi-engine';
import { Download, Printer } from 'lucide-react';
import { useMemo, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { Notice } from '../../components/ui/Notice';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/cn';
import { downloadText } from '../../lib/csv';
import { serverMessage } from '../../lib/form-helpers';
import { PILLAR_CLASSES, PILLAR_META, PILLARS } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { weekDateLabel } from '../tracker/tracker-display';
import { DataTable } from './DataTable';
import {
  buildReportCsv,
  type CustomRange,
  DEFAULT_PERIOD_CHOICE,
  groupTitle,
  PERIOD_CHOICES,
  type PeriodChoice,
  periodFor,
  rangeLabel,
  reportCsvFileName,
  showsTargetLine,
} from './reports-display';
import { SummaryTable } from './SummaryTable';
import { TrendChart } from './TrendChart';

const today = (): string => new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * The weekly steering report (plan §7). Filters choose the period (current quarter by default), a pillar, the KPIs
 * (chips grouped by pillar) and whether inactive KPIs are included; `reports.steering` answers with every number
 * already resolved by the shared engine and scoped to the caller's pillars. The page renders a summary, one trend
 * chart per unit and currency, the weekly data table, a wide CSV and a print layout. Nothing here is editable.
 */
export const ReportsPage = () => {
  const { t } = useI18nTranslation();

  const [choice, setChoice] = useState<PeriodChoice>(DEFAULT_PERIOD_CHOICE);
  const [custom, setCustom] = useState<CustomRange | null>(null);
  const [rangeWarning, setRangeWarning] = useState<string | null>(null);
  const [pillar, setPillar] = useState<Pillar | ''>('');
  const [includeInactive, setIncludeInactive] = useState(false);
  /** `null` = every selectable KPI (the default, and what a pillar or inactive change resets to). */
  const [selected, setSelected] = useState<ReadonlySet<string> | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const weeksQuery = useQuery(orpc.weeks.list.queryOptions({ input: {} }));
  const allWeeks = useMemo(() => weeksQuery.data?.items ?? [], [weeksQuery.data]);

  // Every KPI the caller may see (the service scopes it); the pillar and inactive filters apply in the browser.
  const kpisQuery = useQuery(orpc.kpis.list.queryOptions({ input: { includeInactive: true } }));
  const visibleKpis = useMemo(() => kpisQuery.data?.items ?? [], [kpisQuery.data]);
  const visiblePillars = useMemo(() => PILLARS.filter((each) => visibleKpis.some((kpi) => kpi.pillar === each)), [visibleKpis]);
  const selectable = useMemo(
    () => visibleKpis.filter((kpi) => (pillar === '' || kpi.pillar === pillar) && (includeInactive || kpi.status === 'ACTIVE')),
    [visibleKpis, pillar, includeInactive],
  );
  const selectedIds = useMemo(
    () => (selected === null ? selectable.map((kpi) => kpi.id) : selectable.filter((kpi) => selected.has(kpi.id)).map((kpi) => kpi.id)),
    [selected, selectable],
  );

  const period = periodFor(choice, custom);
  const reportInput: SteeringReportInput | null =
    period && selectedIds.length > 0
      ? { period, pillar: pillar || undefined, kpiIds: selected === null ? undefined : selectedIds, includeInactive }
      : null;
  const reportQuery = useQuery({
    ...orpc.reports.steering.queryOptions({ input: reportInput ?? { period: { kind: 'QUARTER' }, includeInactive: false } }),
    enabled: reportInput !== null && kpisQuery.isSuccess,
    placeholderData: keepPreviousData,
  });
  const report = reportInput ? reportQuery.data : undefined;

  const kpiById = useMemo(() => new Map((report?.kpis ?? []).map((kpi) => [kpi.id, kpi])), [report]);
  const rowById = useMemo(() => new Map((report?.rows ?? []).map((row) => [row.kpiId, row])), [report]);
  const weekById = useMemo(
    () => new Map([...(report?.weeks ?? []), ...(report?.previousWeek ? [report.previousWeek] : [])].map((week) => [week.id, week])),
    [report],
  );

  const scopeChanged = (): void => {
    setSelected(null);
    setNotice(null);
  };

  const onChoice = (next: PeriodChoice): void => {
    setRangeWarning(null);
    setChoice(next);

    if (next === 'CUSTOM' && custom === null) {
      // Start from what is on screen, else the current quarter.
      const ids = report ? { fromId: report.period.fromWeekId, toId: report.period.toWeekId } : currentQuarterWeekIds(allWeeks, new Date());

      setCustom(ids ? { fromWeekId: ids.fromId, toWeekId: ids.toId } : null);
    }
  };

  const onCustomChange = (fromWeekId: string, toWeekId: string): void => {
    const checked = weeksBetween(allWeeks, fromWeekId, toWeekId);

    // Refused, never swapped or shortened: the selects stay on the last valid range.
    if (!checked.ok) {
      setRangeWarning(checked.message);

      return;
    }

    setRangeWarning(null);
    setCustom({ fromWeekId, toWeekId });
  };

  const toggle = (kpiId: string): void => {
    const next = new Set(selectedIds);

    if (!next.delete(kpiId)) {
      next.add(kpiId);
    }

    setSelected(next);
  };

  const onExport = (): void => {
    if (!report) {
      return;
    }

    downloadText(reportCsvFileName(report.weeks), buildReportCsv(report));
    setNotice(t('reports.exported', { kpis: report.kpis.length, weeks: report.weeks.length }));
  };

  if (weeksQuery.isPending || kpisQuery.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('app.loading')}
      </div>
    );
  }

  if (weeksQuery.isError || kpisQuery.isError || allWeeks.length === 0) {
    return <Notice tone="danger">{allWeeks.length === 0 && !weeksQuery.isError ? t('reports.noWeeks') : t('errors.loadFailed')}</Notice>;
  }

  const weekOptions = allWeeks.map((week) => ({
    value: week.id,
    label: `${weekDateLabel(week.weekDate)} · W${String(week.weekNumber).padStart(2, '0')}`,
  }));
  const chipGroups = PILLARS.map((each) => ({ pillar: each, kpis: selectable.filter((kpi) => kpi.pillar === each) })).filter(
    (group) => group.kpis.length > 0,
  );
  const chosen = new Set(selectedIds);

  return (
    <div
      className="space-y-4"
      data-reports-page
    >
      <div className="no-print flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('pages.reports')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('reports.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!report}
            onClick={onExport}
          >
            <Download className="size-3.5" />
            {t('reports.exportCsv')}
          </Button>
          <Button
            size="sm"
            disabled={!report}
            onClick={() => window.print()}
          >
            <Printer className="size-3.5" />
            {t('reports.print')}
          </Button>
        </div>
      </div>

      <div className="no-print flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
        <Select
          aria-label={t('reports.period')}
          data-period-select
          value={choice}
          options={PERIOD_CHOICES.map((each) => ({ value: each, label: t(`reports.periods.${each}`) }))}
          onChange={(event) => onChoice(event.target.value as PeriodChoice)}
          className="h-8 w-44 text-xs"
        />

        {choice === 'CUSTOM' && custom ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <label
              htmlFor="report-from-week"
              className="font-medium"
            >
              {t('reports.from')}
            </label>
            <Select
              id="report-from-week"
              value={custom.fromWeekId}
              options={weekOptions}
              hasError={rangeWarning !== null}
              onChange={(event) => onCustomChange(event.target.value, custom.toWeekId)}
              className="h-8 w-44 text-xs"
            />
            <span aria-hidden>→</span>
            <label
              htmlFor="report-to-week"
              className="font-medium"
            >
              {t('reports.to')}
            </label>
            <Select
              id="report-to-week"
              value={custom.toWeekId}
              options={weekOptions}
              hasError={rangeWarning !== null}
              onChange={(event) => onCustomChange(custom.fromWeekId, event.target.value)}
              className="h-8 w-44 text-xs"
            />
          </div>
        ) : null}

        <Select
          aria-label={t('reports.pillar')}
          data-pillar-select
          value={pillar}
          options={[
            { value: '', label: t('reports.allPillars') },
            ...visiblePillars.map((each) => ({ value: each, label: PILLAR_META[each].label })),
          ]}
          onChange={(event) => {
            setPillar(event.target.value as Pillar | '');
            scopeChanged();
          }}
          className="h-8 w-48 text-xs"
        />

        <Checkbox
          id="report-include-inactive"
          label={t('reports.includeInactive')}
          checked={includeInactive}
          onChange={(event) => {
            setIncludeInactive(event.target.checked);
            scopeChanged();
          }}
        />

        <span className="ms-auto text-xs text-muted-foreground">{t('reports.selectedCount', { count: selectedIds.length })}</span>
      </div>

      {rangeWarning || notice || reportQuery.isError ? (
        <div className="no-print space-y-2">
          {rangeWarning ? (
            <Notice
              tone="danger"
              onDismiss={() => setRangeWarning(null)}
            >
              <span data-range-warning>{rangeWarning}</span>
            </Notice>
          ) : null}
          {reportQuery.isError ? <Notice tone="danger">{serverMessage(reportQuery.error) ?? t('errors.loadFailed')}</Notice> : null}
          {notice ? (
            <Notice
              tone="success"
              onDismiss={() => setNotice(null)}
            >
              {notice}
            </Notice>
          ) : null}
        </div>
      ) : null}

      <div className="no-print rounded-lg border border-border bg-card px-4 py-3">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('reports.kpisInReport')}</p>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(null)}
            >
              {t('reports.selectAll')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set())}
            >
              {t('reports.clear')}
            </Button>
          </div>
        </div>
        <div className="max-h-48 space-y-2 overflow-y-auto">
          {chipGroups.length === 0 ? <p className="text-sm text-muted-foreground">{t('reports.noKpisAvailable')}</p> : null}
          {chipGroups.map((group) => (
            <div
              key={group.pillar}
              className="flex flex-wrap items-center gap-1.5"
            >
              <span className={cn('w-24 shrink-0 text-xs font-semibold', PILLAR_CLASSES[group.pillar].text)}>
                {PILLAR_META[group.pillar].shortLabel}
              </span>
              {group.kpis.map((kpi) => {
                const isOn = chosen.has(kpi.id);

                return (
                  <button
                    key={kpi.id}
                    type="button"
                    data-kpi-chip={kpi.code}
                    aria-pressed={isOn}
                    onClick={() => toggle(kpi.id)}
                    className={cn(
                      'rounded-md border px-2 py-1 text-xs transition-colors',
                      isOn
                        ? cn(PILLAR_CLASSES[group.pillar].band, PILLAR_CLASSES[group.pillar].border, 'text-foreground')
                        : 'border-border bg-background text-muted-foreground hover:bg-muted',
                      kpi.status === 'INACTIVE' && 'opacity-70',
                    )}
                  >
                    {kpi.name}
                    {kpi.status === 'INACTIVE' ? ` (${t('reports.inactive')})` : ''}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {selectedIds.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">{t('reports.selectKpis')}</div>
      ) : !report ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          {t('app.loading')}
        </div>
      ) : (
        <article
          className="print-document space-y-6"
          data-print-document
        >
          <section
            data-report-section="summary"
            className="space-y-3"
          >
            <header>
              <h2 className="text-lg font-semibold">{t('reports.documentTitle')}</h2>
              <p
                className="text-xs text-muted-foreground"
                data-report-range
              >
                {t('reports.generated', { range: rangeLabel(report.weeks), count: report.kpis.length, date: today() })}
              </p>
            </header>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('reports.sections.summary')}</h3>
            <SummaryTable
              kpis={report.kpis}
              rows={report.rows}
              weekById={weekById}
            />
          </section>

          <section
            data-report-section="charts"
            className="print-break-before space-y-3"
          >
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('reports.sections.charts')}</h3>
            <p className="text-xs text-muted-foreground">{t('reports.chart.gaps')}</p>
            {report.groups.length === 0 ? <p className="text-sm text-muted-foreground">{t('reports.noNumericKpis')}</p> : null}
            <div className="print-chart-grid grid gap-4 xl:grid-cols-2">
              {report.groups.map((group) => {
                const single = group.series.length === 1 ? group.series[0] : undefined;
                const singleRow = single ? rowById.get(single.kpiId) : undefined;
                const unit = group.unit;
                const format = (value: number): string => (isNumericUnit(unit) ? formatValue(unit, value, group.currency) : String(value));

                return (
                  <TrendChart
                    key={group.key}
                    groupKey={group.key}
                    title={groupTitle(group)}
                    weeks={report.weeks}
                    series={group.series.map((series) => ({
                      kpiId: series.kpiId,
                      name: kpiById.get(series.kpiId)?.name ?? '',
                      points: series.points,
                    }))}
                    target={
                      singleRow?.target && showsTargetLine(singleRow)
                        ? { value: singleRow.target.value, label: t('reports.chart.target', { value: format(singleRow.target.value) }) }
                        : null
                    }
                    formatPoint={format}
                  />
                );
              })}
            </div>
          </section>

          <section
            data-report-section="table"
            className="print-break-before space-y-3"
          >
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('reports.sections.table')}</h3>
            <DataTable
              kpis={report.kpis}
              weeks={report.weeks}
              values={report.values}
            />
          </section>
        </article>
      )}
    </div>
  );
};
